import React, { useState, useCallback, useEffect, useMemo } from "react";

import {
  ArrowButton,
  InfoPanel,
  ColumnSortEnum,
  Table,
  TableMetadata,
  useTable,
} from "@king-ict/eupisi-ui/components";
import { getInstitutionDisplayNameAndTown } from "@king-ict/eupisi-ui/utils";
import { unwrapResult } from "@reduxjs/toolkit";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { searchInstitutions } from "../../api/kindergartenApi";
import { useAppDispatch } from "../../app/store";
import InstitutionDetails from "../../models/Institution/InstitutionDetails";
import {
  InstitutionFilters,
  InstitutionSearchItem,
} from "../../models/Institution/InstitutionSearch";
import InstitutionSortEnum from "../../models/Institution/InstitutionSortEnum";
import PagedResult from "../../models/Shared/PagedResult";
import { institutionLocationsAndProgramsUrl } from "../../routes/routes";
import { getInstitutionDetalis } from "../../slices/institutionSlice";
import { InstitutionDetailsModal } from "./InstitutionDetailsModal";
import { InstitutionSearchFilters } from "./InstitutionsFilters";
import style from "./InstitutionsPage.module.scss";

export default function InstitutionPage() {
  const [filters, setFilters] = useState<InstitutionFilters>(
    {} as InstitutionFilters
  );

  const [detailsModalOpen, setDetailsModalOpen] = React.useState(false);

  const [institutions, setInstitutions] = useState<
    PagedResult<InstitutionSearchItem>
  >(({
    rowCount: 0,
    results: [],
  } as unknown) as PagedResult<InstitutionSearchItem>);

  const [
    institutionDetails,
    setInstitutionDetails,
  ] = useState<InstitutionDetails>({} as InstitutionDetails);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const handleOpenDetails = useCallback(
    (item: InstitutionSearchItem) => {
      dispatch(getInstitutionDetalis(item.pedagogicalYearInstitutionId))
        .then(unwrapResult)
        .then((res) => {
          setInstitutionDetails(res);
        });
      setDetailsModalOpen(true);
    },
    [dispatch]
  );

  const handlerCloseDetails = () => {
    setDetailsModalOpen(false);
    setInstitutionDetails({} as InstitutionDetails);
  };

  const handleRedirectToDetails = useCallback(
    (item: InstitutionSearchItem) => {
      history.push({
        pathname: institutionLocationsAndProgramsUrl(
          item.pedagogicalYearInstitutionId
        ),
        state: {
          fromInstitutionsPage: true,
        },
      });
    },
    [history]
  );

  const institutionTableMetadata = useMemo(
    () =>
      ({
        rowKey: "pedagogicalYearInstitutionId",
        columns: [
          {
            columnKey: "institutionColumn",
            header: t("common.institution"),
            cellClassName: `${style.tableNameColumn}`,
            headerClassName: `${style.tableNameColumn}`,
            onCellClick: handleOpenDetails,
            accessor: (item) =>
              getInstitutionDisplayNameAndTown(item.name, item.town),
            sortBy: InstitutionSortEnum.Institution,
          },
          {
            columnKey: "townColumn",
            header: t("institutionSearchPage.town"),
            onCellClick: handleOpenDetails,
            accessor: "municipality",
            sortBy: InstitutionSortEnum.Municipality,
          },
          {
            columnKey: "countyColumn",
            header: t("common.county"),
            onCellClick: handleOpenDetails,
            accessor: "county",
            sortBy: InstitutionSortEnum.County,
          },
          {
            columnKey: "founderTypeColumn",
            header: t("institutionSearchPage.founderType"),
            onCellClick: handleOpenDetails,
            accessor: "founderType",
            sortBy: InstitutionSortEnum.FounderType,
          },
          {
            columnKey: "pedagogicalYearInstitutionId",
            accessor: (d) => (
              <ArrowButton
                ariaLabel={t("accessibility.backButton")}
                onClick={() => {
                  handleRedirectToDetails(d);
                }}
              ></ArrowButton>
            ),
          },
        ],
      } as TableMetadata<InstitutionSearchItem>),
    [handleOpenDetails, handleRedirectToDetails, t]
  );

  const [
    tableProps,
    tableState,
    setTableFilters,
  ] = useTable<InstitutionFilters>({
    metadata: institutionTableMetadata,
    data: institutions.results,
    totalItems: institutions.rowCount,
    defaultSort: {
      sortBy: InstitutionSortEnum.Institution,
      order: ColumnSortEnum.ASC,
    },
    pagination: true,
    useQueryParams: true,
    setFiltersFromQueryParams: setFilters,
    filtersTypeDefinition: {
      countyId: Number,
      municipalityId: Number,
      name: String,
      programAgeId: Number,
      programId: Number,
      requestTypeId: Number,
      preselectedInstitution: Number,
    },
  });

  useEffect(() => {
    (async () => {
      const res = await searchInstitutions(tableState);
      setInstitutions(res);
    })();
  }, [dispatch, tableState]);

  const handleSearch = () => {
    setTableFilters({
      ...filters,
    });
  };

  return (
    <>
      <Row className={style.infoMessageRow}>
        <Col md={12}>
          <InfoPanel
            message={t("institutionSearchPage.mainInfoMessage")}
            color="success"
            fullWidth
          />
        </Col>
      </Row>
      <InstitutionSearchFilters
        handleSearch={handleSearch}
        filters={filters}
        setFilters={setFilters}
      ></InstitutionSearchFilters>
      <div>
        <Table {...tableProps} />
      </div>
      {detailsModalOpen ? (
        <InstitutionDetailsModal
          institutionDetails={institutionDetails}
          detailsModalOpen={detailsModalOpen}
          handlerCloseDetails={handlerCloseDetails}
        ></InstitutionDetailsModal>
      ) : (
        <></>
      )}
    </>
  );
}
